import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, FileImage, Phone, User, Calendar, MapPin, ExternalLink, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import GlassCard from "@/components/GlassCard";
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { useState } from "react";
import FilePreviewModal from "@/components/FilePreviewModal";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MaintenanceDetail = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { item } = location.state || {};
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const { deleteServiceLog } = useServiceLogs();

    if (!item) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] flex items-center justify-center">
                <p className="text-muted-foreground">No item data found</p>
            </div>
        );
    }

    const handleCall = () => {
        if (item.contactPhone) {
            window.open(`tel:${item.contactPhone.replace(/\s/g, "")}`, "_self");
        }
    };

    const handleViewDocument = () => {
        if (item.invoiceUrl) {
            setIsPreviewOpen(true);
        }
    };

    const isImageUrl = item.invoiceUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(item.invoiceUrl);
    const isPdfUrl = item.invoiceUrl && /\.pdf$/i.test(item.invoiceUrl);
    const hasDocument = !!item.invoiceUrl;

    const handleDelete = async () => {
        setIsDeleting(true);
        const success = await deleteServiceLog(item.id);
        setIsDeleting(false);
        if (success) {
            navigate("/home-records");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-40 bg-primary/95 backdrop-blur-sm" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(-1)}
                        className="text-primary-foreground hover:bg-primary-foreground/10"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-base font-medium text-primary-foreground">
                        Service Details
                    </h1>
                </div>
            </header>

            {/* Main Content */}
            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 5rem)" }}>
                <div className="max-w-lg mx-auto space-y-6">
                    {/* Title Section */}
                    <div className="text-center pt-4">
                        <h2 className="text-xl font-bold text-foreground">{item.name}</h2>
                        <p className="text-muted-foreground mt-1">{item.details}</p>
                        {item.cost && (
                            <p className="text-lg font-semibold text-primary mt-2">
                                R{item.cost.toLocaleString()}
                            </p>
                        )}
                    </div>

                    {/* Work Description */}
                    {item.description && (
                        <GlassCard className="p-5">
                            <h3 className="font-semibold text-foreground mb-3">Work Description</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {item.description}
                            </p>
                        </GlassCard>
                    )}

                    {/* Document Section */}
                    <GlassCard className="p-5">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-xl bg-primary/10">
                                {isImageUrl ? (
                                    <FileImage className="w-5 h-5 text-primary" />
                                ) : (
                                    <FileText className="w-5 h-5 text-primary" />
                                )}
                            </div>
                            <h3 className="font-semibold text-foreground">Attached Document</h3>
                        </div>

                        {hasDocument ? (
                            <div className="space-y-4">
                                {isImageUrl && (
                                    <div className="rounded-xl overflow-hidden border border-border">
                                        <img
                                            src={item.invoiceUrl}
                                            alt="Receipt/Invoice"
                                            className="w-full h-48 object-cover"
                                        />
                                    </div>
                                )}
                                {isPdfUrl && (
                                    <div className="bg-background/30 rounded-2xl p-6 border border-border/30 text-center">
                                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-sm font-medium text-foreground">PDF Document</p>
                                        <p className="text-xs text-muted-foreground mt-1">Tap to view</p>
                                    </div>
                                )}
                                {!isImageUrl && !isPdfUrl && (
                                    <div className="bg-background/30 rounded-2xl p-6 border border-border/30 text-center">
                                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-sm font-medium text-foreground">Document Available</p>
                                    </div>
                                )}
                                <Button
                                    variant="outline"
                                    className="w-full rounded-xl"
                                    onClick={handleViewDocument}
                                >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    View Full Document
                                </Button>
                            </div>
                        ) : (
                            <div className="bg-muted/30 rounded-2xl p-6 border border-border/30 text-center">
                                <FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                                <p className="text-sm text-muted-foreground">No document attached</p>
                            </div>
                        )}
                    </GlassCard>

                    {/* Service Provider Contact */}
                    {item.merchant && (
                        <GlassCard className="p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-xl bg-primary/10">
                                    <User className="w-5 h-5 text-primary" />
                                </div>
                                <h3 className="font-semibold text-foreground">Service Provider</h3>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm text-foreground">{item.merchant}</span>
                                </div>
                                {item.contactPhone && (
                                    <div className="flex items-center gap-3">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm text-foreground">{item.contactPhone}</span>
                                    </div>
                                )}
                                {item.purchaseDate && (
                                    <div className="flex items-center gap-3">
                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm text-foreground">
                                            Service Date: {new Date(item.purchaseDate).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {item.contactPhone && (
                                <div className="mt-5">
                                    <Button className="w-full rounded-xl" onClick={handleCall}>
                                        <Phone className="w-4 h-4 mr-2" />
                                        Call Provider
                                    </Button>
                                </div>
                            )}
                        </GlassCard>
                    )}

                    {/* Delete Section */}
                    <div className="pt-8 pb-12">
                        <Button
                            variant="destructive"
                            className="w-full gap-2 rounded-2xl h-14"
                            onClick={() => setIsDeleteDialogOpen(true)}
                            disabled={isDeleting}
                        >
                            <Trash2 className="w-5 h-5" />
                            {isDeleting ? "Deleting..." : "Delete Record"}
                        </Button>
                        <p className="text-center text-xs text-muted-foreground mt-3">
                            This will permanently remove this service record from your history.
                        </p>
                    </div>
                </div>
            </main>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Service Record?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this service record for "{item.name}"?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <FilePreviewModal
                open={isPreviewOpen}
                onOpenChange={setIsPreviewOpen}
                fileUrl={item.invoiceUrl}
                fileName={item.name || "Service Document"}
            />
        </div>
    );
};

export default MaintenanceDetail;
